import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';
import { ButtonModule } from '@openng/optimus-ui/button';
import { TooltipModule } from '@openng/optimus-ui/tooltip';
import {
  ActionMenuComponent,
  ActionMenuItem,
} from '../menu/action-menu.component';
import { RowAction } from './table';

@Component({
  selector: 'core-row-action-trigger',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    'data-row-action': '',
  },
  imports: [ButtonModule, TooltipModule],
  template: `
    <div class="flex justify-end px-1">
      @if (actions().length <= 2) {
        @for (action of visibleActions(); track action.id) {
          <p-button
            [attr.data-testid]="'row-action-' + action.id"
            [icon]="action.materialIcon ? undefined : action.icon"
            [disabled]="action.disabled?.(row()) ?? false"
            [plain]="true"
            [rounded]="true"
            [text]="true"
            [attr.aria-label]="action.label"
            [pTooltip]="action.label"
            (click)="executeAction($event, action)"
          >
            @if (action.materialIcon) {
              <span
                class="material-icons"
                style="font-size: 1.2rem"
                aria-hidden="true"
                >{{ action.materialIcon }}</span
              >
            }
          </p-button>
        }
      } @else {
        <p-button
          data-testid="row-actions-button"
          icon="pi pi-ellipsis-v"
          [plain]="true"
          [rounded]="true"
          [text]="true"
          (click)="openMenu($event)"
          aria-label="More row actions"
          i18n-aria-label="@@label-more-row-actions"
        ></p-button>
      }
    </div>
  `,
})
export class RowActionTriggerComponent<T> {
  public readonly row = input.required<T>();
  public readonly actions = input.required<RowAction<T>[]>();
  public readonly menu = input.required<ActionMenuComponent>();
  protected readonly visibleActions = computed(() =>
    this.actions().filter(action => action.visible?.(this.row()) ?? true),
  );
  private readonly menuItems = computed<ActionMenuItem[]>(() =>
    this.visibleActions().map(action => ({
      id: action.id,
      label: action.label,
      icon: action.icon,
      materialIcon: action.materialIcon,
      disabled: action.disabled?.(this.row()) ?? false,
      execute: () => action.execute(this.row()),
    })),
  );

  protected executeAction(event: MouseEvent, action: RowAction<T>): void {
    event.stopPropagation();
    void action.execute(this.row());
  }

  protected openMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.menu().open(event, this.menuItems());
  }
}
